<template>
  <div class="checkout-modal-wrapper">
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-xl modal-fullscreen-lg-down checkout-modal-dialog" role="document">
        <div class="modal-content shadow rounded">
          <div class="modal-header border-0 pb-2">
            <div class="d-flex align-items-center w-100">
              <div class="icon-circle me-3">
                <AppIcon name="bi-check2-circle" class="bi bi-check2-circle" />
              </div>
              <div class="flex-grow-1">
                <div class="modal-title fw-bold mb-0">{{ $t('order_completed') || 'Order Completed' }}</div>
              </div>
              <button type="button" class="btn-close" @click="$emit('new-order')"></button>
            </div>
          </div>

          <div class="modal-body pt-2">
            <div class="row g-3">
              <!-- Left Side - Transaction Information -->
              <div class="col-12 col-lg-5">
                <div class="info-section">
                  <!-- Transaction Details Card -->
                  <div class="info-card mb-2">
                    <div class="d-flex justify-content-between align-items-center info-card-title-wrapper">
                      <h6 class="info-card-title mb-0">
                        <AppIcon name="bi-receipt" class="bi bi-receipt me-2" />{{ $t('transaction_details') || 'Transaction Details' }}
                      </h6>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('reference') || 'Reference' }}:</span>
                      <span class="info-value fw-bold">#{{ details?.reference || '-' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('date') || 'Date' }}:</span>
                      <span class="info-value">{{ details?.date || '-' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('cashier') || 'Cashier' }}:</span>
                      <span class="info-value">{{ details?.cashier?.name || '-' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('client') || 'Client' }}:</span>
                      <span class="info-value">{{ localClient?.name || $t('walk_in') || 'Walk-in' }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('payment_method') || 'Payment Method' }}:</span>
                      <span class="info-value text-capitalize">{{ getPaymentMethodLabel(localPaymentMethod) }}</span>
                    </div>
                  </div>

                  <!-- Summary Card -->
                  <div class="info-card mb-2">
                    <h6 class="info-card-title">
                      <AppIcon name="bi-calculator" class="bi bi-calculator me-2" />{{ $t('summary') || 'Summary' }}
                    </h6>
                    <div class="info-row">
                      <span class="info-label">{{ $t('subtotal') || 'Subtotal' }}:</span>
                      <span class="info-value">{{ details?.subtotal }}</span>
                    </div>
                    <div class="info-row" v-if="details?.discount && details.discount !== '0' && details.discount !== '0.00'">
                      <span class="info-label">{{ $t('discount') || 'Discount' }}:</span>
                      <span class="info-value text-danger">-{{ details.discount }}</span>
                    </div>
                    <div class="info-row">
                      <span class="info-label">{{ $t('vat') || 'VAT' }}:</span>
                      <span class="info-value">{{ details?.tax }}</span>
                    </div>
                    <div class="info-row-total">
                      <span class="info-label-total">{{ $t('total') || 'TOTAL' }}:</span>
                      <span class="info-value-total">{{ details?.total }}</span>
                    </div>
                  </div>

                  <!-- Notes Card -->
                  <div class="info-card">
                    <collapsible-notes-field
                      v-model="localNotes"
                      id="checkout-result-notes"
                      rows="3"
                      :placeholder="$t('add_notes') || 'Add notes...'"
                      @input="hasChanges = true"
                    />
                    <button 
                      v-if="hasChanges"
                      type="button" 
                      class="btn btn-primary btn-sm w-100 mt-2" 
                      @click="updateTransaction"
                      :disabled="updating"
                    >
                      <span v-if="updating" class="spinner-border spinner-border-sm me-1"></span>
                      <AppIcon name="bi-check-circle" v-else class="bi bi-check-circle me-1" />
                      {{ updating ? ($t('saving') || 'Saving...') : ($t('save_notes') || 'Save Notes') }}
                    </button>
                  </div>

                </div>
              </div>

              <!-- Right Side - Receipt Preview -->
              <div class="col-12 col-lg-7">
                <div class="receipt-preview-section">
                  <div class="receipt-preview-header mb-2">
                    <h6 class="mb-0">
                      <AppIcon name="bi-eye" class="bi bi-eye me-2" />{{ $t('receipt_preview') || 'Receipt Preview' }}
                    </h6>
                  </div>
                  <div class="receipt-preview">
                    <div v-if="renderedReceipt" v-html="renderedReceipt" class="receipt-container"></div>
                    <div v-else class="text-center text-muted py-5">
                      <div class="spinner-border" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">{{ $t('loading_receipt') || 'Loading receipt...' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer border-0 pt-0">
            <div class="d-flex w-100 gap-2 checkout-actions">
              <button type="button" class="btn btn-secondary btn-lg flex-grow-1" @click="$emit('new-order')">
                <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{ $t('cancel') || 'Cancel' }}
              </button>
              <button type="button" class="btn btn-success btn-lg flex-grow-1" @click="$emit('print')">
                <AppIcon name="bi-printer-fill" class="bi bi-printer-fill me-1" />{{ $t('print_receipt') || 'Print Receipt' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Client Modal -->
    <search-or-add-client 
      v-if="createClientState"  
      :state="createClientState"  
      @saveClient="saveClient"
    />
  </div>
</template>

<script>
import helper from '../helper';
import SearchOrAddClient from './SearchOrAddClient.vue';

export default {
  name: 'CheckoutResultModal',
  components: {
    SearchOrAddClient
  },
  props: {
    state: { type: Boolean, required: true },
    details: { type: Object, required: true },
    transactionId: { type: String, default: null },
    paymentMethod: { type: String, default: 'cash' },
    notes: { type: String, default: '' },
    client: { type: Object, default: null },
    business: { type: Object, default: null }
  },
  data() {
    return {
      createClientState: false,
      localPaymentMethod: this.paymentMethod,
      localNotes: this.notes,
      localClient: this.client,
      hasChanges: false,
      updating: false,
      showEditOptions: false,
      renderedReceipt: null
    };
  },
  watch: {
    paymentMethod(newVal) {
      this.localPaymentMethod = newVal;
    },
    notes(newVal) {
      this.localNotes = newVal;
    },
    client(newVal) {
      this.localClient = newVal;
    },
    details: {
      immediate: true,
      handler() {
        this.renderReceipt();
      }
    }
  },
  methods: {
    async renderReceipt() {
      try {
        const template = (await this.loadReceiptTemplate()) || this.getDefaultReceiptTemplate();
        const data = this.buildReceiptData();
        this.renderedReceipt = this.renderTemplate(template, data);
      } catch (error) {
       alert('Error rendering receipt: ' + error.message);
        // Fallback to default template
        const template = this.getDefaultReceiptTemplate();
        const data = this.buildReceiptData();
        this.renderedReceipt = this.renderTemplate(template, data);
      }
    },
    
    async loadReceiptTemplate() {
      try {
        const resp = await helper.customGet(`${process.env.VUE_APP_API}/templates`);
        if (resp?.data?.data?.length) {
          const existing = resp.data.data.find(t => (t.type === 'pos_receipt'));
          if (existing && existing.data) {
            let templateData = existing.data;
            if (typeof templateData === 'string') {
              try {
                templateData = JSON.parse(templateData);
              } catch (e) {
                return templateData;
              }
            }
            if (templateData.template) {
              return templateData.template;
            }
            return typeof templateData === 'string' ? templateData : JSON.stringify(templateData);
          }
        }
        return null;
      } catch (e) {
        alert('Failed to load receipt template: ' + e.message);
        return null;
      }
    },
    
    buildReceiptData() {
      // Parse discount to check if it's actually greater than 0
      const discountRaw = this.details?.discount || '0.00';
      const discountValue = parseFloat(String(discountRaw).replace(/[^0-9.-]/g, ''));
      const hasDiscount = discountValue > 0;
      
      return {
        business: this.business || this.details?.business || {
          name: 'Business Name',
          address: '',
          phone: '',
          email: '',
          taxId: '',
          logo: ''
        },
        receipt_id: this.details?.reference || '',
        pos_id: this.details?.reference || '',
        cashier: this.details?.cashier || { name: '' },
        date: this.details?.date || new Date().toLocaleString(),
        client: this.localClient || { name: '' },
        subtotal: this.details?.subtotal || '0.00',
        discount: discountRaw,
        has_discount: hasDiscount,
        tax: this.details?.tax || '0.00',
        tax_rate: this.business.tax || '20%',
        total: this.details?.total || '0.00',
        payment_method: this.getPaymentMethodLabel(this.localPaymentMethod),
        items: (this.details?.items || []).map(item => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          line_total: item.line_total
        })),
        //translation
        cashier_label : this.$t('cashier'),
        product_label : this.$t('product_label'),
        product_quantity : this.$t('product_quantity'),
        price_label : this.$t('product_price'),
        subtotal_label : this.$t('subtotal'),
        discount_label : this.$t('discount'),
        tax_label : this.$t('tax'),
        total_label : this.$t('total'), 
      };
    },
    
    getDefaultReceiptTemplate() {
      return `
<div style="width:280px; margin:0 auto; font-family: Arial, sans-serif; color:#000">
  <div style="text-align:center; margin-bottom:8px">
    <div style="font-weight:bold">{{business.name}}</div>
    <div style="font-size:12px">{{business.address}}</div>
    <div style="font-size:12px">{{business.phone}}</div>
    <div style="font-size:12px; margin-top:4px">{{date}}</div>
    <div style="font-size:12px; margin-top:4px">Id:{{pos_id}}</div>
  </div>
  <hr/>
  <div style="font-size:11px; margin-bottom:8px">
    <div style="display:flex; justify-content:space-between"><span>Receipt ID:</span><span>{{receipt_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>POS ID:</span><span>{{pos_id}}</span></div>
    <div style="display:flex; justify-content:space-between"><span>{{cashier_label}}:</span><span>{{cashier.name}}</span></div>
  </div>
  <hr/>
  <table style="width:100%; font-size:12px">
    <thead>
      <tr>
        <th style="text-align:left">{{product_label}}</th>
        <th style="text-align:center">{{product_quantity}}</th>
        <th style="text-align:right">{{price_label}}</th>
        <th style="text-align:right">{{total_label}}</th>
      </tr>
    </thead>
    <tbody>
      {{#items}}
      <tr>
        <td style="text-align:left">{{name}}</td>
        <td style="text-align:center">{{qty}}</td>
        <td style="text-align:right">{{price}}</td>
        <td style="text-align:right">{{line_total}}</td>
      </tr>
      {{/items}}
    </tbody>
  </table>
  <hr/>
  <div style="font-size:12px">
    <div style="display:flex; justify-content:space-between"><span>{{subtotal_label}}:</span><span>{{subtotal}}</span></div>
    {{#if_discount}}<div style="display:flex; justify-content:space-between"><span>{{discount_label}}:</span><span>{{discount}}</span></div>{{/if_discount}}
    <div style="display:flex; justify-content:space-between"><span>{{tax_label}}:</span><span>{{tax}}</span></div>
    <div style="display:flex; justify-content:space-between; font-weight:bold"><span>{{total_label}}:</span><span>{{total}}</span></div>
  </div>
  <div style="text-align:center; margin-top:8px; font-size:12px"></div>
</div>`;
    },
    
    renderTemplate(tpl, data) {
      if (!tpl) return '';
      let output = String(tpl);
      
      // Process items loop - matches {{#items}}...{{/items}}
      const loopRegex = /\{\{#items\}\}([\s\S]*?)\{\{\/items\}\}/g;
      output = output.replace(loopRegex, (_match, inner) => {
        if (!Array.isArray(data.items)) return '';
        return data.items.map(item => this.replaceTokens(inner, Object.assign({}, data, item))).join('');
      });
      
      // Process discount conditional - matches {{#if_discount}}...{{/if_discount}}
      const discountRegex = /\{\{#if_discount\}\}([\s\S]*?)\{\{\/if_discount\}\}/g;
      output = output.replace(discountRegex, (_match, inner) => {
        // Check both has_discount flag and discount value
        if (data.has_discount !== undefined) {
          return data.has_discount ? inner : '';
        }
        // Fallback: parse discount value
        const discountValue = parseFloat(String(data.discount || '0').replace(/[^0-9.-]/g, ''));
        return discountValue > 0 ? inner : '';
      });
      
      // Inject special components
      output = this.injectSpecialComponents(output, data);
      
      // Replace simple tokens
      output = this.replaceTokens(output, data);
      
      return output;
    },
    
    injectSpecialComponents(html, data) {
      // Inject Logo
      if (data.business && data.business.logo) {
        html = html.replace(/\{\{LOGO\}\}/g, `<img src="${data.business.logo}" style="max-width:120px; max-height:80px; display:block; margin:0 auto 8px;">`);
      } else {
        html = html.replace(/\{\{LOGO\}\}/g, '');
      }
      
      // Inject Barcode
      const barcodeDataUrl = this.generateBarcode(data.pos_id || data.receipt_id);
      html = html.replace(/\{\{BARCODE\}\}/g, `<img src="${barcodeDataUrl}" style="max-width:100%; height:50px; display:block; margin:8px auto;">`);
      
      // Inject QR Code
      const qrData = `Receipt:${data.pos_id}\nTotal:${data.total}\nDate:${data.date}`;
      const qrDataUrl = this.generateQRCode(qrData);
      html = html.replace(/\{\{QRCODE\}\}/g, `<img src="${qrDataUrl}" style="width:100px; height:100px; display:block; margin:8px auto;">`);
      
      return html;
    },
    
    generateBarcode(text) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 200;
        canvas.height = 50;
        
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'black';
        
        let x = 10;
        const barWidth = 2;
        for (let i = 0; i < text.length; i++) {
          const code = text.charCodeAt(i);
          const pattern = code % 2 === 0 ? [1, 0, 1, 0, 1] : [1, 1, 0, 0, 1];
          pattern.forEach(bar => {
            if (bar === 1) {
              ctx.fillRect(x, 5, barWidth, 30);
            }
            x += barWidth;
          });
        }
        
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(text, canvas.width / 2, 45);
        
        return canvas.toDataURL();
      } catch (e) {
        return '';
      }
    },
    
    generateQRCode(text) {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const size = 100;
        const moduleSize = 5;
        canvas.width = size;
        canvas.height = size;
        
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        ctx.fillStyle = 'black';
        
        for (let i = 0; i < size; i += moduleSize) {
          for (let j = 0; j < size; j += moduleSize) {
            const hash = (text.charCodeAt((i + j) % text.length) + i + j) % 2;
            if (hash === 0) {
              ctx.fillRect(i, j, moduleSize, moduleSize);
            }
          }
        }
        
        return canvas.toDataURL();
      } catch (e) {
        return '';
      }
    },
    
    replaceTokens(str, data) {
      return str.replace(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g, (m, key) => {
        const parts = key.split('.');
        let val = data;
        for (const p of parts) {
          if (val && Object.prototype.hasOwnProperty.call(val, p)) {
            val = val[p];
          } else {
            return m;
          }
        }
        return val || '';
      });
    },
    
    getPaymentMethodLabel(method) {
      const labels = {
        cash: this.$t('cash') || 'Cash',
        transfer: this.$t('transfer') || 'Transfer',
        cheque: this.$t('cheque') || 'Cheque',
        credit_card: this.$t('credit_card') || 'Credit Card'
      };
      return labels[method] || method;
    },
    parsePrice(priceString) {
      // Helper to convert formatted price string back to number
      if (typeof priceString === 'number') return priceString;
      if (!priceString) return 0;
      // Remove currency symbols and convert to number
      return parseFloat(String(priceString).replace(/[^0-9.-]/g, '')) || 0;
    },
    openClientModal() {
      this.createClientState = true;
    },
    saveClient(data) {
      if (data.client) {
        this.localClient = data.client;
        this.hasChanges = true;
      }
      if (data && data.state !== undefined) {
        this.createClientState = data.state;
      }
    },
    restoreTransaction() {
      // Emit event to restore the transaction for modification
      this.$emit('restore-transaction', {
        transactionId: this.transactionId,
        details: this.details,
        items: this.details?.items || []
      });
    },
    async updateTransaction() {
      if (!this.transactionId) {
        window.vueOpener.open({
          message: 'No transaction ID available',
          type: 'error',
          position: 'bottom',
        });
        return;
      }

      try {
        this.updating = true;
        
        const payload = {
          payment_method: this.localPaymentMethod,
          notes: this.localNotes,
          client_id: this.localClient?.id || null
        };

        const transactionId = this.transactionId.replace('PO-', '');
        await helper.customPatch(`${process.env.VUE_APP_API}/pos/${transactionId}`, payload, { showToast: false });
        
        this.hasChanges = false;
        this.updating = false;
        
        // Emit event to parent to update its data
        this.$emit('transaction-updated', {
          paymentMethod: this.localPaymentMethod,
          notes: this.localNotes,
          client: this.localClient
        });
      } catch (error) {
        this.updating = false;
        window.vueOpener.open({
          message: 'Failed to update transaction',
          type: 'error',
          position: 'bottom',
        });
      }
    }
  },
  mounted() {
    // Disable background scroll when modal is open
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = 'hidden';
    }
    // Render receipt on mount
    this.renderReceipt();
  },
  beforeDestroy() {
    // Re-enable scroll when modal is closed
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = '';
    }
  }
}
</script>

<style scoped>
/* Modal Styling */
.checkout-modal-wrapper { position: relative; }
.modal-backdrop { position: fixed; top:0; left:0; width:100vw; height:100vh; background: rgba(0,0,0,0.5); z-index: 1040; }
.modal { z-index: 1050; }
.modal-content { border: none; }
.checkout-modal-dialog { 
  max-width: 1200px;
}

@media (min-width: 1400px) {
  .checkout-modal-dialog {
    max-width: 1400px;
  }
}

/* Header */
.icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(25, 135, 84, 0.15), rgba(25, 135, 84, 0.05));
  color: #198754;
  font-size: 1.25rem;
}

/* Left Side - Info Section */
.info-section {
  height: 100%;
}

.info-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.info-card-title-wrapper {
  margin-bottom: 0.5rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid #f3f4f6;
}

/* Extra small button */
.btn-xs {
  font-size: 0.75rem;
  line-height: 1;
  border-radius: 0.25rem;
}

.info-card-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  padding-bottom: 0.4rem;
  border-bottom: 2px solid #f3f4f6;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0;
  border-bottom: 1px dashed #e5e7eb;
  font-size: 0.8rem;
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  color: #6b7280;
  font-weight: 500;
  font-size: 0.8rem;
}

.info-value {
  color: #111827;
  font-weight: 500;
  font-size: 0.8rem;
}

.info-row-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6rem 0;
  margin-top: 0.4rem;
  border-top: 2px solid #e5e7eb;
  font-size: 1rem;
}

.info-label-total {
  color: #111827;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.85rem;
}

.info-value-total {
  color: #059669;
  font-weight: 700;
  font-size: 1.1rem;
}

/* Notes Content */
.notes-textarea {
  font-size: 0.85rem;
  resize: vertical;
  min-height: 60px;
}

.notes-textarea:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

/* Right Side - Receipt Preview Section */
.receipt-preview-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.receipt-preview-header {
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border-radius: 8px 8px 0 0;
  border: 1px solid #e5e7eb;
  border-bottom: none;
}

.receipt-preview-header h6 {
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
}

.receipt-preview {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: .5rem 1rem;
  background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
  border: 1px solid #e5e7eb;
  border-radius: 0 0 8px 8px;
  min-height: 500px;
  max-height: 70vh;
  overflow-y: auto;
}

.receipt-container {
  background: white;
  padding: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  width: 100%;
  max-width: 380px;
}

/* Footer Actions */
.checkout-actions .btn {
  min-height: 48px;
  font-weight: 600;
  font-size: 0.95rem;
}

.checkout-actions .btn i {
  font-size: 1.1rem;
}

/* Custom Scrollbar */
.info-section::-webkit-scrollbar,
.receipt-preview::-webkit-scrollbar {
  width: 6px;
}

.info-section::-webkit-scrollbar-track,
.receipt-preview::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 10px;
}

.info-section::-webkit-scrollbar-thumb,
.receipt-preview::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 10px;
}

.info-section::-webkit-scrollbar-thumb:hover,
.receipt-preview::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Responsive */
@media (max-width: 991px) {
  .receipt-preview {
    min-height: 400px;
    max-height: 500px;
  }
  
  .info-section {
    max-height: none;
    padding-right: 0;
  }
}

@media (max-width: 576px) {
  .receipt-container {
    padding: 1rem 0.75rem;
    max-width: 100%;
  }
  
  .checkout-modal-dialog {
    max-width: 100%;
  }
}

@media print {
  .receipt-preview {
    background: white;
    border: none;
  }
  
  .modal-header,
  .modal-footer,
  .info-section,
  .receipt-preview-header,
  .btn-close {
    display: none !important;
  }
  
  .col-12.col-lg-5 {
    display: none !important;
  }
  
  .col-12.col-lg-7 {
    width: 100%;
    max-width: 100%;
  }
  
  .receipt-container {
    box-shadow: none;
    max-width: 100%;
  }
}
</style>

<style>
/* Global styles for rendered receipt content (not scoped) */
.receipt-container hr {
  border: 0;
  border-top: 1px dashed #333;
  margin: 0.5rem 0;
}

.receipt-container table {
  border-collapse: collapse;
}

.receipt-container table th,
.receipt-container table td {
  padding: 0.25rem 0.5rem;
}

.receipt-container img {
  max-width: 100%;
  height: auto;
}
</style>
